// MENU LATERAL AGRUPADO do topo enxuto (utils/topoContato → topoEnxuto; hoje só o cs2606).
// Painel que entra pela DIREITA sobre um overlay, com os links em 3 grupos (Encontre seu imóvel ·
// Para proprietários · <empresa>) e os contatos no pé. Fecha no X, no overlay, no ESC, ao clicar
// num item e ao trocar de rota; trava a rolagem da página enquanto aberto e devolve o foco ao ☰.
// Itens = dados do tenant (transações, páginas do CRM, blog, telefone, Instagram) — nenhuma rota
// inventada. Cores = paleta do tenant (cor10 = fundo do menu lateral comum, cor1 = rótulos) com
// contraste garantido. Renderizado em portal no <body>: o header usa transform, e um position:fixed
// dentro dele ficaria preso à caixa do header.
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaInstagram, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { ensureReadableTextColor } from '../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../utils/metaPixel';
import { resolveSiteWhatsAppUrl } from '../../utils/site2524WhatsApp';
import { buildContatoTopoUrl } from '../../utils/topoContato';
import { appendCodsiteQuery } from '../../utils/url';
import { useAuth } from '../context/Info';
import styles from './MenuLateralAgrupado.module.css';

export type ItemMenuEnxuto = { label: string; href: string; externo?: boolean; novaAba?: boolean };

const normalizar = (v: any) =>
    String(v ?? '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');

// Páginas do CRM que são assunto do proprietário (hoje o 2606 só tem "Anuncie seu imóvel"; se ele
// criar "Administração de imóveis", ela cai aqui sozinha).
const EH_DO_PROPRIETARIO = /anuncie|administra|proprietari|cadastr[eo] (seu|de) imovel|avali/;
// Política de Privacidade fica só no rodapé.
const EH_POLITICA = /politica|privacidade/;

/** Página "Anuncie seu imóvel" do CRM (ou null) — usada pelo botão do topo e pelo menu. */
export function paginaAnuncie(menus: any[]): ItemMenuEnxuto | null {
    const m = (menus || []).find((x: any) => x?.Disponivel && x?.titulo && /anuncie/.test(normalizar(x.titulo)));
    return m ? itemDaPagina(m) : null;
}

function itemDaPagina(m: any): ItemMenuEnxuto {
    const link = m?.link ? String(m.link) : '';
    return {
        label: String(m.titulo),
        href: link || `/menu/${m.titulo}`,
        externo: Boolean(link && !link.startsWith('/')),
        novaAba: m?.Destino === '_blank',
    };
}

/** Links da barra: transações (Comprar/Alugar…) + Empreendimentos quando o site os exibe. */
export function linksPrincipais(site: any): ItemMenuEnxuto[] {
    const itens: ItemMenuEnxuto[] = [];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        const label = low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : String(t);
        itens.push({ label, href: `/imoveis/${low}/-/-/-/-` });
    });
    if (site?.exibir_empreendimento) itens.push({ label: 'Empreendimentos', href: '/condominios' });
    return itens;
}

/** Grupos do menu lateral a partir dos dados do tenant. Sem página → o item não aparece. */
export function montarGruposMenu(site: any, menus: any[], temBlog: boolean, contato: string) {
    const proprietarios: ItemMenuEnxuto[] = [];
    const paginasEmpresa: ItemMenuEnxuto[] = [];
    const vistos = new Set<string>();
    (menus || [])
        .filter((m: any) => m?.Disponivel && m?.titulo)
        .forEach((m: any) => {
            const chave = normalizar(m.titulo);
            if (EH_POLITICA.test(chave) || vistos.has(chave)) return;
            vistos.add(chave);
            (EH_DO_PROPRIETARIO.test(chave) ? proprietarios : paginasEmpresa).push(itemDaPagina(m));
        });
    const empresa: ItemMenuEnxuto[] = [];
    // "Quem somos" = página /empresa da plataforma, a menos que o cliente tenha a própria.
    if (!vistos.has('quem somos')) empresa.push({ label: 'Quem somos', href: '/empresa' });
    empresa.push(...paginasEmpresa);
    if (temBlog) empresa.push({ label: 'Blog', href: '/blog' });
    if (contato) empresa.push({ label: 'Contato', href: contato, externo: true, novaAba: contato.startsWith('http') });
    return { encontre: linksPrincipais(site), proprietarios, empresa };
}

function formatarTelefone(tel: any): string {
    const d = String(tel ?? '').replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return String(tel ?? '');
}

function arrobaInstagram(url: any): string {
    const m = String(url ?? '').match(/instagram\.com\/([^/?#]+)/i);
    return m ? `@${m[1]}` : 'Instagram';
}

interface IMenuLateralAgrupado {
    site: any;
    aberto: boolean;
    onFechar: () => void;
    institucional: string;
    idPainel: string;
}

const MenuLateralAgrupado: React.FC<IMenuLateralAgrupado> = ({ site, aberto, onFechar, institucional, idPainel }) => {
    const router = useRouter();
    const { allDataHome } = useAuth();
    const painelRef = useRef<HTMLDivElement>(null);
    const fecharRef = useRef<HTMLButtonElement>(null);
    const onFecharRef = useRef(onFechar);
    onFecharRef.current = onFechar;
    const codsite = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;

    // aberto: trava a rolagem (sem "pulo" da barra de rolagem), foco no X, ESC fecha, Tab circula
    // dentro do painel. Ao fechar, devolve tudo como estava e o foco a quem abriu (o ☰).
    useEffect(() => {
        if (!aberto) return;
        const html = document.documentElement;
        const body = document.body;
        const antes = { html: html.style.overflow, body: body.style.overflow, pad: body.style.paddingRight };
        const barra = window.innerWidth - html.clientWidth;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        if (barra > 0) body.style.paddingRight = `${barra}px`;
        const quemAbriu = document.activeElement as HTMLElement | null;
        fecharRef.current?.focus({ preventScroll: true });
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onFecharRef.current();
                return;
            }
            if (e.key !== 'Tab' || !painelRef.current) return;
            const focaveis = Array.from(painelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
            if (!focaveis.length) return;
            const primeiro = focaveis[0];
            const ultimo = focaveis[focaveis.length - 1];
            if (e.shiftKey && document.activeElement === primeiro) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primeiro.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            html.style.overflow = antes.html;
            body.style.overflow = antes.body;
            body.style.paddingRight = antes.pad;
            quemAbriu?.focus?.({ preventScroll: true });
        };
    }, [aberto]);

    // navegação por qualquer caminho (inclusive voltar do navegador) fecha o menu.
    useEffect(() => {
        const fechar = () => onFecharRef.current();
        router.events?.on('routeChangeStart', fechar);
        return () => router.events?.off('routeChangeStart', fechar);
    }, [router.events]);

    if (typeof document === 'undefined') return null;

    const corFundo = site?.cor10 || '#1f1f1f';
    const corTexto = ensureReadableTextColor(site?.cor13, corFundo) || '#ffffff';
    const corRotulo = ensureReadableTextColor(site?.cor1, corFundo, 3, corTexto, corTexto) || corTexto;

    const textoPadrao = site?.texto_botao_whats
        ? String(site.texto_botao_whats)
        : `Olá, estou visitando o site da ${site?.Dados?.empresa ?? ''}, gostaria de um atendimento personalizado. Pode me ajudar?`;
    const tel1 = site?.Dados?.tel1;
    const whatsHref = site?.Dados?.tel1_whats
        ? buildContatoTopoUrl({ telefone: tel1, ehWhatsapp: true, texto: textoPadrao, resolveWhats: (fb) => resolveSiteWhatsAppUrl(site, fb) })
        : '';
    const telHref = buildContatoTopoUrl({ telefone: tel1, ehWhatsapp: false, texto: '' });
    const instagram = site?.Dados?.instagram ? String(site.Dados.instagram) : '';

    const grupos = montarGruposMenu(site, allDataHome?.menus || [], Boolean(allDataHome?.blogs?.[0]?.id), whatsHref || telHref);
    const comCodsite = (href: string) => (href.startsWith('/') ? appendCodsiteQuery(href, codsite) : href);

    const renderItem = (item: ItemMenuEnxuto, idx: number) =>
        item.externo ? (
            <a
                key={idx}
                href={item.href}
                className={styles.item}
                target={item.novaAba ? '_blank' : undefined}
                rel={item.novaAba ? 'noopener noreferrer' : undefined}
                onClick={onFechar}
            >
                {item.label}
            </a>
        ) : (
            <Link key={idx} prefetch={false} href={comCodsite(item.href)} className={styles.item} onClick={onFechar}>
                {item.label}
            </Link>
        );

    const grupo = (titulo: string, itens: ItemMenuEnxuto[]) =>
        itens.length ? (
            <div className={styles.grupo}>
                <p className={styles.rotulo} style={{ color: corRotulo }}>
                    {titulo}
                </p>
                <nav aria-label={titulo} className={styles.lista}>
                    {itens.map(renderItem)}
                </nav>
            </div>
        ) : null;

    const contatoRodape = (metodo: string) =>
        trackMetaEvent(site, 'Contact', { content_name: 'Menu lateral', contact_method: metodo, content_category: 'header' });

    return createPortal(
        <div className={`${styles.raiz} ${aberto ? styles.raizAberta : ''}`} aria-hidden={!aberto}>
            <div className={styles.overlay} onClick={onFechar} />
            <div
                id={idPainel}
                ref={painelRef}
                className={styles.painel}
                role="dialog"
                aria-modal="true"
                aria-label="Menu"
                style={{ background: corFundo, color: corTexto }}
            >
                <div className={styles.cabecalho}>
                    <span className={styles.titulo} style={{ color: corRotulo }}>
                        Menu
                    </span>
                    <button ref={fecharRef} type="button" className={styles.fechar} onClick={onFechar} aria-label="Fechar menu" style={{ color: corTexto }}>
                        <MdClose aria-hidden="true" />
                    </button>
                </div>

                <div className={styles.corpo}>
                    {grupo('Encontre seu imóvel', grupos.encontre)}
                    {grupo('Para proprietários', grupos.proprietarios)}
                    {grupo(institucional, grupos.empresa)}
                </div>

                {whatsHref || telHref || instagram ? (
                    <div className={styles.contatos}>
                        {whatsHref ? (
                            <a href={whatsHref} target="_blank" rel="noopener noreferrer" className={styles.contato} onClick={() => contatoRodape('whatsapp')}>
                                <FaWhatsapp aria-hidden="true" />
                                <span>
                                    <small className={styles.contatoRotulo}>WhatsApp</small>
                                    {formatarTelefone(tel1)}
                                </span>
                            </a>
                        ) : null}
                        {telHref ? (
                            <a href={telHref} className={styles.contato} onClick={() => contatoRodape('phone')}>
                                <FaPhoneAlt aria-hidden="true" />
                                <span>
                                    <small className={styles.contatoRotulo}>Telefone</small>
                                    {formatarTelefone(tel1)}
                                </span>
                            </a>
                        ) : null}
                        {instagram ? (
                            <a href={instagram} target="_blank" rel="noopener noreferrer" className={styles.contato}>
                                <FaInstagram aria-hidden="true" />
                                <span>
                                    <small className={styles.contatoRotulo}>Instagram</small>
                                    {arrobaInstagram(instagram)}
                                </span>
                            </a>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>,
        document.body
    );
};

export default MenuLateralAgrupado;
